import { merge } from '../../helper';

import concat from '../../general/concat';
import inArray from '../../general/inArray';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';

import * as exTable from '../table';

// ----------------------------------------------------------------------------

function start(element: HTMLTableElement, config: Record<string, any> = {}): void {
    // Default Config
    const defConfig = {
        tbody: {},
        stripe: true,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    // Read
    read(element, defConfig);

    // Data
    data(element, defConfig);
}

// ----------------------------------------------------------------------------

function read(element: HTMLElement, config: Record<string, any> = {}): void {
    // Get field count
    const fieldCount = exTable.getFieldCount(element);
    // Set column count
    config.columnCount = fieldCount;

    // If using columns
    if (config.columns && config.columns.length >= config.columnCount) {
        config.columnCount = config.columns.length;
    }
    // If using data
    else if (config.data?.length) {
        const firstRow = config.data[0];

        if (Array.isArray(firstRow)) {
            config.columnCount = firstRow.length;
        } else if (firstRow?.constructor === Object) {
            config.columnCount = Object.keys(firstRow).length;
        }
    }
}

function data(element: HTMLElement, config: Record<string, any> = {}): void {
    // If there is no column count
    if (!config.columnCount) {
        console.error('Column count is empty...');
        return;
    }
    // If there is no data
    if (!config.data?.length) {
        return;
    }

    const rows = exTable.rows.data(element, {
        ...config,
        data: config.data,
        fnCell: dataCell,
    });

    for (let r = 0; r < rows.length; r++) {
        rows[r].row.setAttribute('row', r.toString());
    }

    // --------------------------------------------------------------------

    // If using column definitions
    columnDefs(element, {
        columnDefs: config.columnDefs,
        rows: rows.map(function (itemRow): HTMLTableRowElement {
            return itemRow.row;
        }),
        data: config.data,
    });
}

// ----------------------------------------------------------------------------

function dataCell(config: Record<string, any>): void {
    if (config.columns && config.columns.length > 0) {
        column(config.element, {
            column: config.columns[config.columnIndex],
            data: config.source,
            value: config.value,
        });
    } else {
        config.element.child(config.value ?? '');
    }
}

// ----------------------------------------------------------------------------

function column(element: HTMLElement, config: Record<string, any> = {}): void {
    let result = config.value ?? '';

    if (config.column?.constructor === Object) {
        const columnConfig = config.column;

        if (typeof columnConfig.data === 'function') {
            result = columnConfig.data(config.data) ?? '';
        } else if (typeof columnConfig.data === 'string' && config.data?.constructor === Object) {
            result = config.data[columnConfig.data] ?? '';
        } else if (columnConfig.data == null && config.data?.constructor === Object) {
            result = '';
        }

        if (typeof columnConfig.render === 'function') {
            result = columnConfig.render.call(element, result);
        } else if (columnConfig.render != null) {
            result = columnConfig.render;
        }
    } else if (config.data?.constructor === Object) {
        result = '';
    }

    element.child(result);
}

function columnDefs(element: HTMLElement, config: Record<string, any> = {}): void {
    // If there is no column definitions
    if (!config.columnDefs) {
        return;
    }
    // If column definitions is no array
    if (config.columnDefs && config.columnDefs.constructor !== Array) {
        return;
    }

    // --------------------------------------------------------------------

    // Foreach Column Definitions
    for (const itemColumnDefs of config.columnDefs) {
        // If item column definitions is empty object
        if (isEmptyObject(itemColumnDefs)) {
            continue;
        }
        // If there is no item definitions
        if (
            !itemColumnDefs.targets &&
            !itemColumnDefs.defaultContent &&
            !itemColumnDefs.render &&
            !itemColumnDefs.visible
        ) {
            continue;
        }

        const targets = itemColumnDefs.targets != null ? concat(itemColumnDefs.targets) : null;

        // ----------------------------------------------------------------

        // Foreach Rows
        for (let r = 0; r < config.rows.length; r++) {
            const elRow = config.rows[r];

            // Foreach Columns
            for (let c = 0; c < elRow.cells.length; c++) {
                if (targets != null && !inArray(c, targets)) {
                    continue;
                }

                const elColumn = elRow.cells[c];
                const itemData = Array.isArray(config.data[r]) ? config.data[r][c] : config.data[r];

                if (!elColumn || isEmptyObject(itemData)) {
                    continue;
                }

                columnDef(itemColumnDefs, elRow, itemData, elColumn);
            }
        }
    }
}

function columnDef(
    itemDefs: Record<string, any>,
    elRow: HTMLTableRowElement,
    itemData: any,
    elColumn: HTMLTableCellElement,
): void {
    if (itemDefs.defaultContent != null) {
        elColumn.child(itemDefs.defaultContent);
    }

    if (typeof itemDefs.render === 'function') {
        elColumn.child(itemDefs.render(itemData, elColumn));
    }

    if (itemDefs.visible === true) {
        elRow.style.display = 'none';
    }
}

// ----------------------------------------------------------------------------

export { start, read, data, dataCell, column, columnDef, columnDefs };
